/**
 * Application configuration and mode detection
 * Clean implementation with 3 distinct modes: normal, preview, edit
 */

export type AppMode = "normal" | "preview" | "edit" | "restaurant";

const TEMPLATE_KEYS = ['pizza', 'culinary_journey', 'eatflow', 'casual_dining'];

/**
 * Detects the current application mode based on URL structure
 * - Normal: http://localhost:3002/
 * - Preview: http://localhost:3002/pizza/preview
 * - Edit: http://localhost:3002/uuid/preview/edit
 * - Restaurant: http://localhost:3002/uuid
 */
export const getAppMode = (): AppMode => {
  if (typeof window === "undefined") return "normal";

  const { pathname } = window.location;

  if (pathname.includes("/preview/edit")) {
    return "edit";
  }
  if (pathname.includes("/preview")) {
    return "preview";
  }
  
  // Check if first segment is a UUID or slug-uuid (restaurant ID mode)
  const pathSegments = pathname.split('/').filter(Boolean);
  if (pathSegments[0]) {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    
    // Check for pure UUID
    if (uuidRegex.test(pathSegments[0])) {
      return "restaurant";
    }
    
    // Check for slug-uuid format (e.g., frenchie-uuid)
    const parts = pathSegments[0].split('-');
    if (parts.length >= 6) {
      const uuidParts = parts.slice(-5).join('-');
      if (uuidRegex.test(uuidParts)) {
        return "restaurant";
      }
    }
  }
  
  return "normal";
};

/**
 * Checks if current path segment is a valid template name
 */
export const isValidTemplate = (segment: string): boolean => {
  return TEMPLATE_KEYS.includes(segment);
};

/**
 * Extracts the restaurant/template ID from the current URL
 */
export const getUrlId = (): string | null => {
  if (typeof window === "undefined") return null;

  const pathSegments = window.location.pathname.split('/').filter(Boolean);
  return pathSegments[0] || null;
};

/**
 * Determines if API calls should be skipped based on mode
 * In preview mode, we avoid real API calls
 */
export const shouldSkipApiCalls = (): boolean => {
  return getAppMode() === "preview";
};
